import { AsyncPipe, DecimalPipe } from "@angular/common";
import { Component, ElementRef, OnInit, ViewChild } from "@angular/core";
import { AngularFirestore } from "@angular/fire/compat/firestore";
import { doc, Firestore, getDoc } from "@angular/fire/firestore";
import { Router } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { IonContent, IonicModule } from "@ionic/angular";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { Chart, registerables } from "chart.js";
import moment from "moment";
import { combineLatest, from, of } from "rxjs";
import { map, switchMap, tap } from "rxjs/operators";
import { CommonVariable } from "src/app/app.constants";
import type { SwiperOptions } from "swiper/types";
import { SwiperModule } from "swiper/angular";
Chart.register(...registerables);

@Component({
  selector: "app-charity-page",
  templateUrl: "./charity-page.component.html",
  styleUrls: ["./charity-page.component.scss"],
  standalone: true,
  imports: [
    IonicModule,
    SwiperModule,
    AsyncPipe,
    DecimalPipe,
    TranslatePipe,
  ],
})
export class CharityPageComponent implements OnInit {
  @ViewChild("doughnutCanvas") private doughnutCanvas: ElementRef;
  @ViewChild("pageTop") pageTop: IonContent;
  doughnutChart: any;
  chartPercent: any = [];
  lastMonthCharityPercent1: number = 50;
  lastMonthCharityPercent2: number = 50;
  lastMonth: string;
  selectedLanguage = "en";
  currentMonthpercent1: number = 50;
  currentMonthpercent2: number = 50;
  ngAfterViewInit() {
    this.doughnutChartMethod();
  }

  doughnutChartMethod() {
    setTimeout(() => {
      if (this.doughnutChart) {
        this.doughnutChart.destroy();
      }

      this.doughnutChart = new Chart(this.doughnutCanvas?.nativeElement, {
        type: "doughnut",
        data: {
          datasets: [
            {
              label: "# of Votes",
              data: [this.lastMonthCharityPercent2, this.lastMonthCharityPercent1],
              backgroundColor: ["rgb(246 151 90)", "rgba(247 119 119)"],
              hoverBackgroundColor: ["#F6975A", "#F77777"],
              borderWidth: 0,
              weight: 1,
            },
          ],
        },
      });
    }, 5000);
  }

  constructor(
    private firestore: Firestore,
    private angularFirestore: AngularFirestore,
    private router: Router,
    public translate: TranslateService,
    private authService: AuthService
  ) {
    this.authService?.selectedlanguage$?.subscribe(
      (l) => {
        const selectedLanguage = l ? l : "en";
        const finalLanguage = CommonVariable.bypassLanguages.includes(selectedLanguage) ? 'en' : selectedLanguage;
        this.selectedLanguage = finalLanguage;
        translate.use(finalLanguage);
      },
      (err) => {
        return err;
      }
    );
  }

  /* for charity data binding*/

  // NOTE: /general_modules/home_card_charity is no longer used
  // Keeping the other charity observables that use the monthly path structure
  charityPercent$ = of(null); // Placeholder - old collection removed

  charity$ = from(
    getDoc(doc(this.firestore, 'translates/en'))
  ).pipe(
    tap(docSnapshot => {
      if (!docSnapshot.exists()) {
        this.authService.errorHandlerLogs('Firestore doc not found at path:', docSnapshot);
      }
    }),
    map(docSnapshot => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        return data?.charity ?? null;  // return charity or null if missing
      }
      return null; // no throw, just return null gracefully
    })
  );

  charityOfTheMonth$ = from(
    getDoc(
      doc(
        this.firestore,
        `/general_modules/charity_of_month/charity_by_month/${moment().format("MMM_YYYY")
          .toLowerCase()}`
      )
    )
  ).pipe(
    switchMap((docSnapshot) => {
      if (docSnapshot.exists()) {
        return of(docSnapshot);
      }
      this.authService.errorHandlerLogs(
        "Current month charity document not found. Falling back to previous month."
      );
      return from(
        getDoc(
          doc(
            this.firestore,
            `/general_modules/charity_of_month/charity_by_month/${moment()
              .subtract(1, "months")
              .format("MMM_YYYY")
              .toLowerCase()}`
          )
        )
      );
    }),
    tap((docSnapshot) => {
      if (!docSnapshot.exists()) {
        this.authService.errorHandlerLogs(
          "No charity document found for the current or previous month."
        );
      }
    }),
    map((docSnapshot) => {
      if (docSnapshot.exists()) {
        return docSnapshot.data();
      }
      return null;
    })
  );

  previousMonthCharity$ = from(
    getDoc(
      doc(
        this.firestore,
        `/general_modules/charity_of_month/charity_by_month/${moment()
          .subtract(1, "months")
          .format("MMM_YYYY")
          .toLowerCase()}`
      )
    )
  ).pipe(
    tap(docSnapshot => {
      if (!docSnapshot.exists()) {
        this.authService.errorHandlerLogs('Previous month charity doc does not exist at:', docSnapshot);
      }
    }),
    map(docSnapshot => {
      if (docSnapshot.exists()) {
        return docSnapshot.data();
      }
      return null;  // Return null if document is missing
    }),
    tap(data => {
      if (data) {
        this.lastMonth = moment().subtract(1, "months").format("MMMM");
        this.lastMonthCharityPercent1 = data?.content?.[0]?.charity_percentage ?? 0;
        this.lastMonthCharityPercent2 = data?.content?.[1]?.charity_percentage ?? 0;
        this.doughnutChartMethod();
      } else {
        // Optionally reset values if no data
        this.lastMonth = '';
        this.lastMonthCharityPercent1 = 0;
        this.lastMonthCharityPercent2 = 0;
      }
    })
  );
  charityQuote$ = from(
    getDoc(
      doc(this.firestore, '/general_modules/system_reference/settings/charity_quotes')
    )
  ).pipe(
    tap(docSnapshot => {
      if (!docSnapshot.exists()) {
        this.authService.errorHandlerLogs('Charity quotes document does not exist at:', docSnapshot);
      }
    }),
    map(docSnapshot => {
      if (docSnapshot.exists()) {
        return docSnapshot.data();
      }
      return null; // Gracefully return null if no document
    })
  );
  charityPageData$ = combineLatest([
    this.charity$,
    this.charityOfTheMonth$,
    this.previousMonthCharity$,
    this.charityQuote$,
    this.charityPercent$,
  ]).pipe(
    map((res) => {
      return res;
    })
  );
  sliderOptions: SwiperOptions = {
    speed: 400,
    initialSlide: 0,
    centeredSlides: true,
    slidesPerView: 1.15
  };

  ngOnInit() { }
  backToHomePage() {
    this.router.navigateByUrl("/mainTabs/tabs/home");
  }

  public pageScroller(pageTop: IonContent) {
    //scroll to page top
    if (!this.pageTop && pageTop) {
      this.pageTop = pageTop;
    } else if (!this.pageTop && !pageTop) {
      return;
    } else {
      this.pageTop?.scrollToTop();
    }
  }
}
